import {
    Brand,
    ImageEmbeddingSearchHint,
    Listing,
    Platform,
    ProductAttributeValue,
    ProductCategory,
    ProductCluster,
    ProductClusterType,
    ProductSubcategory,
    Seller,
    Variant,
    WholesaleTag
} from './domain';
import {
    lookitAttributeValues,
    lookitBrands,
    lookitCategories,
    lookitListings,
    lookitPlatforms,
    lookitProductClusters,
    lookitSellers,
    lookitVariants,
    lookitWholesaleTags
} from './mock-data';

export interface ProductClusterQuery {
    clusterTypes?: ProductClusterType[];
}

export interface VariantQuery {
    productClusterId?: string;
    variantIds?: string[];
}

export interface ProductCatalogSearchByTextHintsInput {
    selectedCategoryId?: string | null;
    subcategoryHint?: ProductSubcategory | string | null;
    brandHints?: ImageEmbeddingSearchHint[];
    ocrHints?: ImageEmbeddingSearchHint[];
    candidateProductIds?: string[];
    limit?: number | null;
}

export interface ProductCatalogRepository {
    getProductClusterById(productClusterId: string): Promise<ProductCluster | null>;
    getListingsForCluster(productClusterId: string): Promise<Listing[]>;
    searchByTextHints(input?: ProductCatalogSearchByTextHintsInput): Promise<ProductCluster[]>;
}

const clusterOrder: ProductClusterType[] = ['brand_exact', 'wholesale_same', 'similar_style'];

function cloneData<T>(data: T): T {
    return JSON.parse(JSON.stringify(data)) as T;
}

function normalizedText(value?: string | null): string {
    return (value || '').trim().toLowerCase();
}

function categoryBaseId(categoryId?: string | null): string | null {
    const [baseId] = (categoryId || '').split(':');
    return baseId || null;
}

function categoryRootForId(categoryId?: string | null): string | null {
    const baseId = categoryBaseId(categoryId);
    if (baseId === 'top') return '상의';
    if (baseId === 'bottom') return '하의';
    if (baseId === 'outer') return '아우터';
    if (baseId === 'dress') return '원피스';
    if (baseId === 'shoes') return '신발';
    if (baseId === 'bag') return '가방';
    if (baseId === 'accessory') return '액세서리';
    return null;
}

function normalizeSubcategory(value?: string | null): ProductSubcategory | string | null {
    const normalizedValue = normalizedText(value);
    if (!normalizedValue) return null;

    const aliases: Record<string, ProductSubcategory> = {
        pants: '팬츠',
        pant: '팬츠',
        trouser: '팬츠',
        trousers: '팬츠',
        slacks: '팬츠',
        '슬랙스': '팬츠',
        '바지': '팬츠',
        '팬츠': '팬츠',
        denim: '데님',
        jeans: '데님',
        jean: '데님',
        '청바지': '데님',
        '데님': '데님',
        skirt: '스커트',
        '스커트': '스커트',
        shorts: '쇼츠',
        short: '쇼츠',
        '쇼츠': '쇼츠',
        '반바지': '쇼츠',
        leggings: '레깅스',
        legging: '레깅스',
        '레깅스': '레깅스',
        blouse: '블라우스',
        '블라우스': '블라우스',
        tshirt: '티셔츠',
        't-shirt': '티셔츠',
        tee: '티셔츠',
        '티셔츠': '티셔츠',
        knit: '니트',
        '니트': '니트',
        shirt: '셔츠',
        '셔츠': '셔츠',
        cardigan: '가디건',
        '가디건': '가디건'
    };

    return aliases[normalizedValue] || value || null;
}

function subcategoryFromCategoryId(categoryId?: string | null): ProductSubcategory | string | null {
    const parts = (categoryId || '').split(':');
    return normalizeSubcategory(parts.length > 1 ? parts.slice(1).join(':') : null);
}

function compatibleSubcategories(subcategory?: ProductSubcategory | string | null): Set<string> | null {
    const normalized = normalizeSubcategory(subcategory);
    if (!normalized) return null;
    if (normalized === '팬츠' || normalized === '데님') return new Set(['팬츠', '데님']);
    return new Set([normalized]);
}

function hintTexts(hints?: ImageEmbeddingSearchHint[]): string[] {
    return (hints || [])
        .map((hint) => normalizedText(hint.normalizedText || hint.text))
        .filter(Boolean);
}

function textContainsAny(value: string | null | undefined, hints: string[]): boolean {
    const text = normalizedText(value);
    if (!text || hints.length === 0) return false;
    return hints.some((hint) => text.includes(hint) || hint.includes(text));
}

function clusterMatchesSearchCategory(
    cluster: ProductCluster,
    input: ProductCatalogSearchByTextHintsInput
): boolean {
    const categoryRoot = categoryRootForId(input.selectedCategoryId);
    if (categoryRoot && cluster.category_root !== categoryRoot) return false;

    const subcategory = normalizeSubcategory(input.subcategoryHint || subcategoryFromCategoryId(input.selectedCategoryId));
    const allowedSubcategories = compatibleSubcategories(subcategory);
    if (!allowedSubcategories) return true;
    if (!cluster.subcategory) return true;
    return allowedSubcategories.has(cluster.subcategory);
}

function textHintScore(cluster: ProductCluster, input: ProductCatalogSearchByTextHintsInput): number {
    const brandHints = hintTexts(input.brandHints);
    const ocrHints = hintTexts(input.ocrHints);
    let score = 0;

    if (textContainsAny(cluster.brand_name, brandHints)) score += 4;
    if (textContainsAny(cluster.wholesale_tag_name, brandHints)) score += 4;
    if (textContainsAny(cluster.canonical_name, brandHints)) score += 2;
    if (textContainsAny(cluster.canonical_name, ocrHints)) score += 2;
    if (textContainsAny(cluster.brand_name, ocrHints)) score += 2;
    if (textContainsAny(cluster.wholesale_tag_name, ocrHints)) score += 2;

    return score;
}

export class InMemoryProductCatalogRepository implements ProductCatalogRepository {
    async getProductClusterById(productClusterId: string): Promise<ProductCluster | null> {
        const cluster = lookitProductClusters.find((item) => item.id === productClusterId) || null;
        return cluster ? cloneData(cluster) : null;
    }

    async getListingsForCluster(productClusterId: string): Promise<Listing[]> {
        return cloneData(lookitListings.filter((listing) => listing.product_cluster_id === productClusterId));
    }

    async searchByTextHints(input: ProductCatalogSearchByTextHintsInput = {}): Promise<ProductCluster[]> {
        const candidateProductIdSet = input.candidateProductIds?.length
            ? new Set(input.candidateProductIds)
            : null;
        const scoredClusters = lookitProductClusters
            .filter((cluster) => !candidateProductIdSet || candidateProductIdSet.has(cluster.id))
            .filter((cluster) => clusterMatchesSearchCategory(cluster, input))
            .map((cluster) => ({
                cluster,
                score: textHintScore(cluster, input)
            }))
            .sort((a, b) => {
                const scoreDiff = b.score - a.score;
                if (scoreDiff !== 0) return scoreDiff;
                const typeDiff = clusterOrder.indexOf(a.cluster.cluster_type) - clusterOrder.indexOf(b.cluster.cluster_type);
                if (typeDiff !== 0) return typeDiff;
                return b.cluster.confidence_level - a.cluster.confidence_level;
            });
        const clusters = scoredClusters.map((item) => item.cluster);
        const limit = typeof input.limit === 'number' && input.limit > 0 ? input.limit : undefined;
        return cloneData(limit ? clusters.slice(0, limit) : clusters);
    }
}

export const productCatalogRepository: ProductCatalogRepository = new InMemoryProductCatalogRepository();

export async function getProductClusters(query: ProductClusterQuery = {}): Promise<ProductCluster[]> {
    const clusterTypes = query.clusterTypes ? new Set(query.clusterTypes) : null;
    const clusters = clusterTypes
        ? lookitProductClusters.filter((cluster) => clusterTypes.has(cluster.cluster_type))
        : lookitProductClusters;

    return cloneData(clusters);
}

export async function getProductCluster(productClusterId: string): Promise<ProductCluster | null> {
    const cluster = lookitProductClusters.find((item) => item.id === productClusterId) || null;
    return cluster ? cloneData(cluster) : null;
}

export async function getProductClusterById(productClusterId: string): Promise<ProductCluster | null> {
    return productCatalogRepository.getProductClusterById(productClusterId);
}

export async function getListingsForCluster(productClusterId: string): Promise<Listing[]> {
    return productCatalogRepository.getListingsForCluster(productClusterId);
}

export async function searchByTextHints(input: ProductCatalogSearchByTextHintsInput = {}): Promise<ProductCluster[]> {
    return productCatalogRepository.searchByTextHints(input);
}

export async function getCategories(): Promise<ProductCategory[]> {
    return cloneData(lookitCategories);
}

export async function getAttributeValues(): Promise<ProductAttributeValue[]> {
    return cloneData(lookitAttributeValues);
}

export async function getBrands(): Promise<Brand[]> {
    return cloneData(lookitBrands);
}

export async function getBrand(brandId: string): Promise<Brand | null> {
    const brand = lookitBrands.find((item) => item.id === brandId) || null;
    return brand ? cloneData(brand) : null;
}

export async function getWholesaleTags(): Promise<WholesaleTag[]> {
    return cloneData(lookitWholesaleTags);
}

export async function getWholesaleTag(wholesaleTagId: string): Promise<WholesaleTag | null> {
    const tag = lookitWholesaleTags.find((item) => item.id === wholesaleTagId) || null;
    return tag ? cloneData(tag) : null;
}

export async function getVariants(query: VariantQuery = {}): Promise<Variant[]> {
    const variantIds = query.variantIds ? new Set(query.variantIds) : null;
    const variants = lookitVariants.filter((variant) => {
        if (query.productClusterId && variant.product_cluster_id !== query.productClusterId) return false;
        if (variantIds && !variantIds.has(variant.id)) return false;
        return true;
    });

    return cloneData(variants);
}

export async function getVariantsByProductCluster(productClusterId: string): Promise<Variant[]> {
    return getVariants({ productClusterId });
}

export async function getPlatforms(): Promise<Platform[]> {
    return cloneData(lookitPlatforms);
}

export async function getPlatform(platformId: string): Promise<Platform | null> {
    const platform = lookitPlatforms.find((item) => item.id === platformId) || null;
    return platform ? cloneData(platform) : null;
}

export async function getSellers(): Promise<Seller[]> {
    return cloneData(lookitSellers);
}

export async function getSeller(sellerId: string): Promise<Seller | null> {
    const seller = lookitSellers.find((item) => item.id === sellerId) || null;
    return seller ? cloneData(seller) : null;
}

export const productService = {
    productCatalogRepository,
    getProductClusters,
    getProductCluster,
    getProductClusterById,
    getListingsForCluster,
    searchByTextHints,
    getCategories,
    getAttributeValues,
    getBrands,
    getBrand,
    getWholesaleTags,
    getWholesaleTag,
    getVariants,
    getVariantsByProductCluster,
    getPlatforms,
    getPlatform,
    getSellers,
    getSeller
};
